import React, { useState } from 'react';
import { DatePicker, Select, Tooltip, message } from 'antd';
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';
import { selectCurrentAdmin } from '@/redux/auth/selectors';
import { useTaskDrawer } from '../TaskDrawerContext';
import Avatar from './Avatar';
import EffortBar from './EffortBar';
import { PRIORITY_OPTIONS, STATUS_OPTIONS } from '../constants';
import { getFullName } from '../utils/personUtils';

const STATUS_DOT = {
    'Backlog': '#94a3b8',
    'In Progress': '#3b82f6',
    'Under Review': '#f59e0b',
    'Completed': '#10b981',
    'Closed': '#10b981',
    'Close': '#10b981',
    'On Hold': '#f97316',
    'Cancelled': '#ef4444',
};

const PRIORITY_COLOR = {
    'Low': '#22c55e',
    'Medium': '#f59e0b',
    'High': '#ef4444',
    'Critical': '#7c3aed',
    'None': '#94a3b8',
};

const DEP_LABELS = {
    finish_to_start: 'Finish → Start',
    start_to_start: 'Start → Start',
    finish_to_finish: 'Finish → Finish',
};

const STANDARD_CATEGORIES = [
    'Website',
    'Graphic Design',
    'Amazon & Ecommerce',
    'Mobile App',
    'Video Animation',
    'Marketing',
    'Development',
    'General',
];

const CATEGORIES_WITH_SUBCATEGORIES = {
    'Website': [
        'Website Development',
        'Website Maintenance',
        'Website Redesign',
        'UI/UX Design',
        'CMS Integration',
        'E-Commerce Website',
    ],
    'Graphic Design': [
        'Logo Design',
        'Stationery Design',
        'Brochure Design',
        'Social Media Design',
        'Brand Identity',
        'Packaging Design',
    ],
    'Amazon & Ecommerce': [
        'Listing Design',
        'Store Management',
        'Amazon Store Setup',
        'Product Research',
        'PPC & Advertising',
        'A+ Content Design',
    ],
    'Mobile App': [
        'iOS App Development',
        'Android App Development',
        'Cross-Platform App',
        'Mobile App Maintenance',
        'App UI/UX Design',
    ],
    'Video Animation': [
        '2D Explainer Video',
        '3D Animation Video',
        'Video Editing',
        'Motion Graphics',
        'Logo Intro Animation',
    ],
    'Marketing': [
        'Social Media Marketing',
        'SEO Optimization',
        'Content Marketing',
        'Email Marketing',
        'PPC Campaign Management',
    ],
    'Development': [
        'Frontend Development',
        'Backend Development',
        'API Integration',
        'Database Optimization',
        'Bug Fixes & QA',
    ],
    'General': [
        'Task',
        'Milestone Deliverable',
        'Operational',
    ],
};

export default function TaskMetadata() {
    const {
        task,
        employees,
        projects,
        projectTasks,
        dependencies,
        additionalProjects,
        displayActualTimeMs,
        remainingTimeMs,
        editPriority,
        setEditPriority,
        editEstHours,
        setEditEstHours,
        editEstMinutes,
        setEditEstMinutes,
        editEffortHours,
        setEditEffortHours,
        editEffortMinutes,
        setEditEffortMinutes,
        statusUpdating,
        handleAssigneeChange,
        handleStartDateChange,
        handleDueDateChange,
        handleStatusChange,
        handleAddDependency,
        handleRemoveDependency,
        handleAddProject,
        handleRemoveAdditionalProject,
        mode,
        handlePrimaryProjectChange,
        milestoneId,
        projectMilestones,
        handleMilestoneChange,
        itemType,
        milestoneOwner,
        setMilestoneOwner,
        completionCriteria,
        setCompletionCriteria,
        deliverables,
        setDeliverables,
    } = useTaskDrawer();

    const [pendingBlocker, setPendingBlocker] = useState('');
    const [pendingType, setPendingType] = useState('finish_to_start');
    const [addingDep, setAddingDep] = useState(false);
    const [showProjectAdder, setShowProjectAdder] = useState(false);
    const [newDeliverableTitle, setNewDeliverableTitle] = useState('');
    const [newChildTaskTitle, setNewChildTaskTitle] = useState('');

    const isMilestone = (task?.itemType === 'milestone') || (itemType === 'milestone');

    const currentAdmin = useSelector(selectCurrentAdmin);
    const currentUserId = currentAdmin?._id || currentAdmin?.id;
    const userRole = currentAdmin?.role || currentAdmin?.adminRole;
    const isElevated = ['super_admin', 'Admin'].includes(userRole);

    const creatorId = task?.createdBy?._id || task?.createdBy;
    const isCreator = currentUserId && creatorId && String(creatorId) === String(currentUserId);

    const resolvePerson = (person) => {
        if (!person) return null;
        if (typeof person === 'object' && person.name) return person;
        const id = typeof person === 'object' ? (person._id || person.id) : person;
        if (id && Array.isArray(employees)) {
            const found = employees.find(e => String(e._id || e.id) === String(id));
            if (found) return found;
        }
        return person;
    };

    const creatorUser = resolvePerson(task?.createdBy);
    const updaterUser = resolvePerson(task?.lastUpdatedBy);

    const projectPMId = task?.project?.projectManager?._id || task?.project?.projectManager;
    const isProjectPM = currentUserId && projectPMId && String(projectPMId) === String(currentUserId);
    const canManagePlanning = true;

    const availableBlockers = (projectTasks || []).filter(
        t => t._id !== task?._id &&
            !(dependencies || []).some(d => String(d.taskId?._id || d.taskId) === String(t._id))
    );

    const primaryProjectSelected = task?.project?._id || task?.project;

    const rawMsId = milestoneId ||
        task?.milestoneId?._id ||
        (typeof task?.milestoneId === 'string' ? task?.milestoneId : null) ||
        (task?.parentId?.itemType === 'milestone' ? (task.parentId._id || task.parentId) : null);
    const currentSelectedMilestoneId = rawMsId ? String(rawMsId) : null;

    const mergedMilestones = React.useMemo(() => {
        const list = [...(projectMilestones || [])];
        const msObj = (task?.milestoneId && typeof task.milestoneId === 'object')
            ? task.milestoneId
            : (task?.parentId && typeof task.parentId === 'object' && task.parentId.itemType === 'milestone')
                ? task.parentId
                : null;
        if (msObj && msObj._id) {
            const idStr = String(msObj._id);
            if (!list.some(m => String(m._id) === idStr)) {
                list.unshift({
                    _id: idStr,
                    title: msObj.title || msObj.name || 'Current Milestone',
                    name: msObj.title || msObj.name || 'Current Milestone',
                    status: msObj.status || 'In Progress',
                });
            }
        } else if (currentSelectedMilestoneId && !list.some(m => String(m._id) === String(currentSelectedMilestoneId))) {
            list.unshift({
                _id: String(currentSelectedMilestoneId),
                title: 'Current Milestone',
                name: 'Current Milestone',
                status: 'In Progress',
            });
        }
        return list;
    }, [projectMilestones, task?.milestoneId, task?.parentId, currentSelectedMilestoneId]);

    const availableProjects = (projects || []).filter(
        p => p._id !== primaryProjectSelected &&
            !(additionalProjects || []).some(ap => ap._id === p._id)
    );

    const hasDeps = (dependencies || []).length > 0;
    const projectCount = (task?.project ? 1 : 0) + (additionalProjects || []).length;
    const hasProjectSelected = Boolean(primaryProjectSelected);

    const handleAddDeliverable = () => {
        if (!newDeliverableTitle.trim()) return;
        const updated = [...(deliverables || []), { title: newDeliverableTitle.trim(), done: false }];
        setDeliverables(updated);
        setNewDeliverableTitle('');
    };

    const handleToggleDeliverable = (index) => {
        const updated = (deliverables || []).map((item, idx) =>
            idx === index ? { ...item, done: !item.done } : item
        );
        setDeliverables(updated);
    };

    const handleRemoveDeliverable = (index) => {
        const updated = (deliverables || []).filter((_, idx) => idx !== index);
        setDeliverables(updated);
    };

    return (
        <section data-tour="task-metadata-grid" className="rounded-2xl bg-[#f5f6f8] dark:bg-[#141414] border border-[#e7e8e9] dark:border-[#303030] p-5 space-y-5 text-[13px] text-[#1c1e21] dark:text-[#e2e8f0]">

            {/* ── Milestone Progress Card (View/Edit Mode) ── */}
            {isMilestone && mode !== 'create' && (
                <div data-tour="milestone-progress-card" className="bg-gradient-to-r from-amber-500/15 via-amber-500/5 to-transparent p-4 rounded-xl border border-amber-300 dark:border-amber-900/50 space-y-2.5">
                    <div className="flex justify-between items-center">
                        <div className="flex items-center gap-2.5">
                            <span className="text-amber-500 font-bold text-xl">◆</span>
                            <div>
                                <span className="text-[11px] font-bold uppercase tracking-wider text-amber-700 dark:text-amber-300">
                                    Milestone Progress
                                </span>
                                <h4 className="font-bold text-sm text-[#1c1e21] dark:text-white">
                                    {task?.completedChildTasks ?? 0} of {task?.totalChildTasks ?? 0} Child Tasks Completed
                                </h4>
                            </div>
                        </div>
                        <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-amber-200 dark:bg-amber-950 text-amber-900 dark:text-amber-200 border border-amber-300 dark:border-amber-800">
                            {task?.progress ?? 0}%
                        </span>
                    </div>

                    <div className="w-full h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                        <div
                            className="h-full bg-emerald-500 rounded-full transition-all duration-500"
                            style={{ width: `${task?.progress ?? 0}%` }}
                        />
                    </div>
                </div>
            )}

            {/* ── Two-Column Task Metadata Grid ── */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4 items-start">

                {/* ── Column 1 (Left): Assignee, Start date, Due date, Status, Priority ── */}
                <div className="space-y-4">
                    {/* ── Assignee / Milestone Owner ── */}
                    <div data-tour="task-assignee-select" className="flex items-center gap-4">
                        <span className="w-28 text-[#65676b] font-normal shrink-0">
                            {isMilestone ? 'Owner' : (
                                <>
                                    Assignee
                                    {mode === 'create' && !isMilestone && <span className="text-red-500 font-bold">*</span>}
                                </>
                            )}
                        </span>
                        <div className="flex-1 min-w-0">
                            {(isMilestone ? (milestoneOwner || task?.milestoneOwner || task?.assignee) : task?.assignee) ? (
                                <div className="flex items-center gap-2 group">
                                    <Avatar person={isMilestone ? (milestoneOwner || task?.milestoneOwner || task?.assignee) : task?.assignee} size={24} />
                                    <span className="font-semibold text-[#1c1e21] dark:text-white truncate">
                                        {getFullName(isMilestone ? (milestoneOwner || task?.milestoneOwner || task?.assignee) : task?.assignee)}
                                    </span>
                                    {canManagePlanning ? (
                                        <button
                                            onClick={() => isMilestone ? setMilestoneOwner(null) : handleAssigneeChange(null)}
                                            className="text-[#65676b] hover:text-[#1c1e21] flex items-center"
                                        >
                                            <span className="material-symbols-outlined text-base">close</span>
                                        </button>
                                    ) : (
                                        <Tooltip title="Only Project Manager or Creator can reassign">
                                            <span className="text-xs text-slate-400 ml-1 cursor-not-allowed">🔒</span>
                                        </Tooltip>
                                    )}
                                </div>
                            ) : canManagePlanning ? (
                                <Select
                                    placeholder={<span className="text-[#bec3c9]">{isMilestone ? 'Select owner' : 'No assignee'}</span>}
                                    className="w-48"
                                    variant="borderless"
                                    showSearch
                                    optionFilterProp="label"
                                    onChange={(val) => {
                                        const selected = employees.find(e => e._id === val);
                                        if (isMilestone) {
                                            setMilestoneOwner(selected || { _id: val });
                                        } else {
                                            handleAssigneeChange(val);
                                        }
                                    }}
                                    value={null}
                                    size="small"
                                    style={{ paddingLeft: 0, marginLeft: '-8px' }}
                                >
                                    {employees.map((employee) => (
                                        <Select.Option key={employee._id} value={employee._id} label={getFullName(employee)}>
                                            <div className="flex items-center gap-2">
                                                <Avatar person={employee} size={18} />
                                                <span className="text-xs">{getFullName(employee)}</span>
                                            </div>
                                        </Select.Option>
                                    ))}
                                </Select>
                            ) : (
                                <Tooltip title="Only Project Manager or Creator can assign team members">
                                    <span className="text-[#94a3b8] italic flex items-center gap-1.5 cursor-not-allowed text-xs">
                                        <span>🔒</span> No assignee
                                    </span>
                                </Tooltip>
                            )}
                        </div>
                    </div>

                    {/* ── Start Date ── */}
                    <div className="flex items-center gap-4">
                        <span className="w-28 text-[#65676b] font-normal shrink-0">
                            Start date
                            {mode === 'create' && !isMilestone && <span className="text-red-500 font-bold">*</span>}
                        </span>
                        <div className="flex-1 min-w-0">
                            {task.startDate ? (
                                <div className="flex items-center gap-2 group">
                                    <span className="material-symbols-outlined text-base text-[#65676b]">calendar_month</span>
                                    <span className="font-semibold text-[#1c1e21] dark:text-white">
                                        {dayjs(task.startDate).format('MMM DD, YYYY')}
                                    </span>
                                    {canManagePlanning ? (
                                        <button
                                            onClick={() => handleStartDateChange(null)}
                                            className="text-[#65676b] hover:text-[#1c1e21] flex items-center"
                                        >
                                            <span className="material-symbols-outlined text-base">close</span>
                                        </button>
                                    ) : (
                                        <Tooltip title="Only Project Manager or Creator can change dates">
                                            <span className="text-xs text-slate-400 ml-1 cursor-not-allowed">🔒</span>
                                        </Tooltip>
                                    )}
                                </div>
                            ) : canManagePlanning ? (
                                <DatePicker
                                    variant="borderless"
                                    format="MMM DD, YYYY"
                                    placeholder="No start date"
                                    size="small"
                                    suffixIcon={<span className="material-symbols-outlined text-base text-[#65676b]">calendar_month</span>}
                                    onChange={handleStartDateChange}
                                    allowClear={false}
                                    style={{ paddingLeft: 0, marginLeft: '-8px' }}
                                />
                            ) : (
                                <Tooltip title="Only Project Manager or Creator can set start date">
                                    <span className="text-[#94a3b8] italic flex items-center gap-1.5 cursor-not-allowed text-xs">
                                        <span>🔒</span> No start date
                                    </span>
                                </Tooltip>
                            )}
                        </div>
                    </div>

                    {/* ── Due Date ── */}
                    <div data-tour="task-due-date-select" className="flex items-center gap-4">
                        <span className="w-28 text-[#65676b] font-normal shrink-0">
                            Due date
                            {mode === 'create' && <span className="text-red-500 font-bold">*</span>}
                        </span>
                        <div className="flex-1 min-w-0">
                            {task.dueDate ? (
                                <div className="flex items-center gap-2 group flex-wrap">
                                    {(() => {
                                        const isOverdue = dayjs(task.dueDate).isBefore(dayjs(), 'day');
                                        const isCompleted = ['Completed', 'Closed', 'Close', 'Cancelled'].includes(task?.status);
                                        const showOverdue = isOverdue && !isCompleted;
                                        const dateClasses = showOverdue
                                            ? 'bg-red-50 text-red-600 border-red-200 dark:bg-red-950/80 dark:text-red-300 dark:border-red-800'
                                            : isOverdue
                                                ? 'bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700'
                                                : 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/80 dark:text-emerald-300 dark:border-emerald-800';
                                        return (
                                            <div
                                                className={`flex items-center gap-1.5 rounded-full px-2.5 py-1 border text-xs font-semibold ${dateClasses}`}
                                            >
                                                <span className="material-symbols-outlined text-base">
                                                    calendar_today
                                                </span>
                                                <span>
                                                    {showOverdue && <span className="mr-1 text-xs">⚠</span>}
                                                    {dayjs(task.dueDate).format('MMM DD, YYYY')}
                                                </span>
                                                {canManagePlanning ? (
                                                    <button
                                                        onClick={() => handleDueDateChange(null)}
                                                        className="flex items-center ml-1 opacity-70 hover:opacity-100 transition-opacity"
                                                    >
                                                        <span className="material-symbols-outlined text-sm font-bold">close</span>
                                                    </button>
                                                ) : (
                                                    <Tooltip title="Only Project Manager or Creator can change deadlines">
                                                        <span className="text-xs text-slate-400 ml-1 cursor-not-allowed">🔒</span>
                                                    </Tooltip>
                                                )}
                                            </div>
                                        );
                                    })()}
                                </div>
                            ) : canManagePlanning ? (
                                <DatePicker
                                    variant="borderless"
                                    format="MMM DD, YYYY"
                                    placeholder="No due date"
                                    size="small"
                                    suffixIcon={<span className="material-symbols-outlined text-base text-[#65676b]">calendar_today</span>}
                                    onChange={handleDueDateChange}
                                    allowClear={false}
                                    style={{ paddingLeft: 0, marginLeft: '-8px' }}
                                />
                            ) : (
                                <Tooltip title="Only Project Manager or Creator can set deadlines">
                                    <span className="text-[#94a3b8] italic flex items-center gap-1.5 cursor-not-allowed text-xs">
                                        <span>🔒</span> No due date
                                    </span>
                                </Tooltip>
                            )}
                        </div>
                    </div>

                    {/* ── Status ── */}
                    <div data-tour="task-status-select" className="flex items-center gap-4">
                        <span className="w-28 text-[#65676b] font-normal shrink-0">
                            Status
                            {mode === 'create' && !isMilestone && <span className="text-red-500 font-bold">*</span>}
                        </span>
                        <div className="flex-1 min-w-0">
                            <Select
                                value={['Closed', 'Close'].includes(task?.status) ? 'Completed' : ((!task?.status || task?.status === 'Draft') ? 'Backlog' : task.status)}
                                onChange={handleStatusChange}
                                variant="borderless"
                                size="small"
                                loading={statusUpdating}
                                className="min-w-[150px]"
                                style={{ paddingLeft: 0, marginLeft: '-8px' }}
                            >
                                {STATUS_OPTIONS.map((option) => (
                                    <Select.Option key={option} value={option}>
                                        <div className="flex items-center gap-2">
                                            <span
                                                className="w-2 h-2 rounded-full shrink-0"
                                                style={{ backgroundColor: STATUS_DOT[option] || '#94a3b8' }}
                                            />
                                            <span className="text-[13px]">{option}</span>
                                        </div>
                                    </Select.Option>
                                ))}
                            </Select>
                        </div>
                    </div>

                    {/* ── Priority ── */}
                    <div data-tour="task-priority-select" className="flex items-center gap-4">
                        <span className="w-28 text-[#65676b] font-normal shrink-0">
                            Priority
                            {mode === 'create' && !isMilestone && <span className="text-red-500 font-bold">*</span>}
                        </span>
                        <div className="flex-1 min-w-0">
                            {canManagePlanning ? (
                                <Select
                                    value={editPriority}
                                    onChange={setEditPriority}
                                    variant="borderless"
                                    size="small"
                                    className="min-w-[130px]"
                                    style={{ paddingLeft: 0, marginLeft: '-8px' }}
                                >
                                    {PRIORITY_OPTIONS.map((option) => (
                                        <Select.Option key={option} value={option}>
                                            <div className="flex items-center gap-2">
                                                <span
                                                    className="w-2 h-2 rounded-full shrink-0"
                                                    style={{ backgroundColor: PRIORITY_COLOR[option] || '#94a3b8' }}
                                                />
                                                <span className="text-[13px]">{option}</span>
                                            </div>
                                        </Select.Option>
                                    ))}
                                </Select>
                            ) : (
                                <Tooltip title="Only Project Manager or Creator can change priority">
                                    <div className="inline-flex items-center gap-2 cursor-not-allowed px-1.5 py-0.5 rounded">
                                        <span
                                            className="w-2 h-2 rounded-full shrink-0"
                                            style={{ backgroundColor: PRIORITY_COLOR[editPriority] || '#94a3b8' }}
                                        />
                                        <span className="text-[13px] font-semibold text-[#1c1e21] dark:text-white">{editPriority || 'None'}</span>
                                        <span className="text-xs text-slate-400">🔒</span>
                                    </div>
                                </Tooltip>
                            )}
                        </div>
                    </div>
                </div>

                {/* ── Column 2 (Right): Created by, Source, Last updated, Completed on, Dependencies ── */}
                <div className="space-y-4">
                    {/* ── Created by ── */}
                    {(creatorUser || task?.createdAt) ? (
                        <div className="flex items-center gap-4">
                            <span className="w-28 text-[#65676b] font-normal shrink-0">Created by</span>
                            <div className="flex-1 min-w-0 flex items-center gap-1.5 flex-wrap">
                                {creatorUser && (
                                    <div className="flex items-center gap-1">
                                        <Avatar person={creatorUser} size={18} />
                                        <span className="font-semibold text-[#1c1e21] dark:text-white truncate max-w-[120px]">
                                            {getFullName(creatorUser) || (typeof creatorUser === 'string' ? 'User' : '')}
                                        </span>
                                    </div>
                                )}
                                {task?.createdAt && (
                                    <span className="text-xs text-[#65676b] dark:text-slate-400">
                                        {creatorUser ? '· ' : ''}{dayjs(task.createdAt).format('MMM D, YYYY, h:mm A')}
                                    </span>
                                )}
                            </div>
                        </div>
                    ) : null}

                    {/* ── Source ── */}
                    {(task?.generationSource || task?.source) ? (
                        <div className="flex items-center gap-4">
                            <span className="w-28 text-[#65676b] font-normal shrink-0">Source</span>
                            <div className="flex-1 min-w-0">
                                {(() => {
                                    const src = (task?.generationSource || task?.source || '').toLowerCase();
                                    const config = {
                                        ai: { label: '⚡ AI Generated', className: 'bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-950/80 dark:text-amber-300 dark:border-amber-800' },
                                        cre: { label: '🏗 CRE Project', className: 'bg-blue-50 text-blue-800 border-blue-200 dark:bg-blue-950/80 dark:text-blue-300 dark:border-blue-800' },
                                        asana: { label: '🔄 Asana Migration', className: 'bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-950/80 dark:text-purple-300 dark:border-purple-800' },
                                        system: { label: '⚙️ System', className: 'bg-emerald-50 text-emerald-800 border-emerald-200 dark:bg-emerald-950/80 dark:text-emerald-300 dark:border-emerald-800' },
                                        manual: { label: '📋 Manual', className: 'bg-slate-50 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700' },
                                    };
                                    const c = config[src] || config.manual;
                                    return (
                                        <span
                                            className={`w-fit inline-block px-2.5 py-0.5 rounded-full text-[11px] font-semibold border ${c.className}`}
                                        >
                                            {c.label}
                                        </span>
                                    );
                                })()}
                            </div>
                        </div>
                    ) : null}

                    {/* ── Last Updated ── */}
                    {task?.updatedAt ? (
                        <div className="flex items-center gap-4">
                            <span className="w-28 text-[#65676b] font-normal shrink-0">Last updated</span>
                            <div className="flex-1 min-w-0 flex items-center gap-1.5 flex-wrap">
                                <span className="material-symbols-outlined text-sm text-[#65676b]">update</span>
                                <span className="font-semibold text-[#1c1e21] dark:text-white text-xs">
                                    {dayjs(task.updatedAt).format('MMM D, YYYY, h:mm A')}
                                </span>
                                {updaterUser && (
                                    <div className="flex items-center gap-1 ml-1">
                                        <Avatar person={updaterUser} size={16} />
                                        <span className="text-xs text-[#65676b] dark:text-slate-400 truncate max-w-[100px]">
                                            {getFullName(updaterUser)}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : null}

                    {/* ── Completed on ── */}
                    {(['Completed', 'Closed', 'Close'].includes(task?.status) || task?.completedOn) ? (
                        <div className="flex items-center gap-4">
                            <span className="w-28 text-[#65676b] font-normal shrink-0">Completed on</span>
                            <div className="flex-1 min-w-0 flex items-center gap-1.5">
                                <span className="material-symbols-outlined text-sm text-emerald-500">check_circle</span>
                                <span className="font-semibold text-emerald-600 dark:text-emerald-400 text-xs">
                                    {dayjs(task?.completedOn || task?.updatedAt).format('MMM D, YYYY, h:mm A')}
                                </span>
                            </div>
                        </div>
                    ) : null}

                    {/* ── Dependencies ── */}
                    <div className="flex items-start gap-4">
                        <span className="w-28 text-[#65676b] font-normal shrink-0 pt-0.5">Dependencies</span>
                        <div className="flex-1 min-w-0 space-y-1.5">
                            {(dependencies || []).map((dep) => {
                                const depTask = dep.taskId && typeof dep.taskId === 'object' ? dep.taskId : null;
                                const depId = String(dep.taskId?._id || dep.taskId);
                                const depTitle = depTask?.title || projectTasks.find(t => t._id === depId)?.title || depId;
                                const depTaskId = depTask?.taskId || projectTasks.find(t => t._id === depId)?.taskId;
                                const depStatus = depTask?.status || projectTasks.find(t => t._id === depId)?.status;
                                const depType = dep.type || 'finish_to_start';

                                return (
                                    <div key={depId} className="flex items-center gap-2 bg-[#f0f2f5] dark:bg-[#202020] border border-[#dddfe2] dark:border-[#333] rounded-lg px-2.5 py-1.5 group">
                                        <span
                                            className="w-2 h-2 rounded-full flex-shrink-0"
                                            style={{ backgroundColor: STATUS_DOT[depStatus] || '#94a3b8' }}
                                        />
                                        <Tooltip title={`${depTitle} (${depTaskId})`} placement="topLeft">
                                            <span className="text-xs font-semibold text-[#1c1e21] dark:text-white flex-1 truncate select-none cursor-help">
                                                {depTitle}
                                            </span>
                                        </Tooltip>
                                        {depTaskId && (
                                            <span className="text-[10px] font-mono text-[#65676b] flex-shrink-0">{depTaskId}</span>
                                        )}
                                        <span className="text-[10px] text-[#006591] bg-[#dff2ff] dark:bg-[#003852] dark:text-[#7fd4ff] px-1.5 py-0.5 rounded-full font-semibold flex-shrink-0">
                                            {DEP_LABELS[depType] || depType}
                                        </span>
                                        <button
                                            onClick={() => handleRemoveDependency(depId)}
                                            className="opacity-0 group-hover:opacity-100 transition-opacity text-[#65676b] hover:text-red-500 shrink-0 ml-1"
                                            title="Remove dependency"
                                        >
                                            <span className="material-symbols-outlined text-sm">close</span>
                                        </button>
                                    </div>
                                );
                            })}

                            {addingDep && (
                                <div className="flex flex-wrap items-center gap-2 bg-white dark:bg-[#1f1f1f] p-2 rounded-lg border border-[#dddfe2] dark:border-[#303030]">
                                    <Tooltip title={pendingBlocker ? (availableBlockers.find(t => t._id === pendingBlocker)?.title || '') : ''} placement="top">
                                        <Select
                                            showSearch
                                            placeholder="Select task…"
                                            value={pendingBlocker || undefined}
                                            onChange={(val) => setPendingBlocker(val)}
                                            optionFilterProp="label"
                                            className="w-40 text-xs"
                                            size="small"
                                            popupClassName="dark:bg-[#1e293b]"
                                        >
                                            {availableBlockers.map(t => (
                                                <Select.Option key={t._id} value={t._id} label={`${t.title} (${t.taskId})`}>
                                                    <div className="text-xs truncate">
                                                        {t.title} ({t.taskId})
                                                    </div>
                                                </Select.Option>
                                            ))}
                                        </Select>
                                    </Tooltip>
                                    <Select
                                        value={pendingType}
                                        onChange={(val) => setPendingType(val)}
                                        className="w-28 text-xs"
                                        size="small"
                                        popupClassName="dark:bg-[#1e293b]"
                                    >
                                        <Select.Option value="finish_to_start">Finish → Start</Select.Option>
                                        <Select.Option value="start_to_start">Start → Start</Select.Option>
                                        <Select.Option value="finish_to_finish">Finish → Finish</Select.Option>
                                    </Select>
                                    <button
                                        onClick={() => {
                                            if (!pendingBlocker) return;
                                            handleAddDependency(pendingBlocker, pendingType);
                                            setPendingBlocker('');
                                            setPendingType('finish_to_start');
                                            setAddingDep(false);
                                        }}
                                        disabled={!pendingBlocker}
                                        className="text-xs font-bold text-white bg-[#03488a] hover:bg-[#003263] disabled:opacity-40 px-2.5 py-1 rounded transition-colors shrink-0"
                                    >
                                        Add
                                    </button>
                                    <button
                                        onClick={() => { setPendingBlocker(''); setAddingDep(false); }}
                                        className="text-[#65676b] hover:text-[#1c1e21] dark:text-slate-400 dark:hover:text-white"
                                    >
                                        <span className="material-symbols-outlined text-sm">close</span>
                                    </button>
                                </div>
                            )}

                            {!addingDep && (
                                <button
                                    onClick={() => setAddingDep(true)}
                                    className="text-xs text-[#65676b] hover:text-[#1c1e21] dark:text-slate-400 dark:hover:text-white font-medium"
                                >
                                    + Add dependency
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* ── Milestone Specific: Completion Criteria ── */}
            {isMilestone && (
                <div data-tour="milestone-completion-criteria" className="flex items-start gap-4 pt-1">
                    <div className="w-28 pt-2">
                        <span className="text-[#65676b] font-normal block">Completion Criteria</span>
                        <span className="text-[10px] text-[#94a3b8] font-normal block leading-tight">PM approval conditions</span>
                    </div>
                    <div className="flex-1 space-y-2">
                        <textarea
                            rows={6}
                            value={completionCriteria}
                            onChange={(e) => setCompletionCriteria(e.target.value)}
                            placeholder="Describe what must be true before this milestone is marked completed (e.g. Final designs completed, reviewed, all revisions addressed, and assets uploaded)."
                            className="w-full bg-white dark:bg-[#1f1f1f] border border-[#dddfe2] dark:border-[#303030] rounded-xl p-3.5 text-sm text-[#1c1e21] dark:text-white focus:ring-2 focus:ring-[#03488a] outline-none transition-all shadow-sm min-h-[140px]"
                        />
                        {/* Quick Suggestions Pills */}
                        <div className="flex flex-wrap gap-1.5 pt-0.5">
                            <span className="text-[11px] font-bold text-slate-400 self-center mr-1">Quick Suggestions:</span>
                            {[
                                'All tasks completed & verified',
                                'Designs reviewed & final assets uploaded',
                                'QA testing passed & staging approved',
                                'Client confirmation & approval received',
                            ].map((suggestion, idx) => (
                                <button
                                    key={idx}
                                    type="button"
                                    onClick={() => {
                                        if (!completionCriteria.trim()) {
                                            setCompletionCriteria(suggestion);
                                        } else if (!completionCriteria.includes(suggestion)) {
                                            setCompletionCriteria(prev => `${prev}\n• ${suggestion}`);
                                        }
                                    }}
                                    className="text-[11px] font-medium bg-slate-200/70 hover:bg-[#03488a] hover:text-white dark:bg-slate-800 dark:hover:bg-[#03488a] text-slate-700 dark:text-slate-200 px-3 py-1.5 rounded-lg transition-all"
                                >
                                    💡 {suggestion}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* ── Milestone Specific: Expected Deliverables ── */}
            {isMilestone && (
                <div data-tour="milestone-deliverables-checklist" className="flex items-start gap-4 pt-1">
                    <span className="w-28 text-[#65676b] font-normal pt-3">Deliverables</span>
                    <div className="flex-1 space-y-2.5">
                        {(deliverables || []).map((item, idx) => (
                            <div key={idx} className="flex items-center gap-2.5 bg-white dark:bg-[#1f1f1f] p-3 rounded-xl border border-[#dddfe2] dark:border-[#303030]">
                                <input
                                    type="checkbox"
                                    checked={item.done}
                                    onChange={() => handleToggleDeliverable(idx)}
                                    className="rounded text-[#03488a] cursor-pointer w-4 h-4"
                                />
                                <span className={`flex-1 text-sm ${item.done ? 'line-through text-slate-400' : 'font-semibold text-[#1c1e21] dark:text-white'}`}>
                                    {item.title || item.name}
                                </span>
                                <button
                                    onClick={() => handleRemoveDeliverable(idx)}
                                    className="text-slate-400 hover:text-red-500 p-1"
                                >
                                    <span className="material-symbols-outlined text-base">close</span>
                                </button>
                            </div>
                        ))}

                        <div className="flex items-center gap-2">
                            <input
                                type="text"
                                value={newDeliverableTitle}
                                onChange={(e) => setNewDeliverableTitle(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddDeliverable())}
                                placeholder="Add deliverable title (e.g. Approved UI Prototype)…"
                                className="flex-1 h-12 bg-white dark:bg-[#1f1f1f] border border-[#dddfe2] dark:border-[#303030] rounded-xl px-4 text-sm outline-none focus:border-[#03488a] shadow-sm"
                            />
                            <button
                                onClick={handleAddDeliverable}
                                disabled={!newDeliverableTitle.trim()}
                                className="h-12 px-5 bg-[#03488a] text-white text-sm font-bold rounded-xl disabled:opacity-40 hover:bg-[#003263] transition-colors shrink-0 shadow-sm flex items-center gap-1.5"
                            >
                                <span className="material-symbols-outlined text-lg">add</span>
                                Add Deliverable
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Projects Section ── */}
            <div data-tour="task-projects-section" className="space-y-3 pt-2">
                <div className="flex items-center gap-2">
                    <h4 className="text-[14px] font-bold text-[#1c1e21]">Projects</h4>
                    <span className="text-[12px] font-bold text-[#65676b] bg-white border border-[#dddfe2] w-5 h-5 rounded-full flex items-center justify-center">
                        {projectCount}
                    </span>

                    {/* Add Project inline button */}
                    {!showProjectAdder && canManagePlanning && (availableProjects.length > 0 || !primaryProjectSelected) && (
                        <button
                            onClick={() => setShowProjectAdder(true)}
                            className="text-[#65676b] hover:text-[#1c1e21] flex items-center ml-1"
                        >
                            <span className="material-symbols-outlined text-lg">add</span>
                        </button>
                    )}

                    {showProjectAdder && (
                        <Select
                            placeholder="Add project…"
                            onChange={(val) => {
                                if (!primaryProjectSelected) {
                                    handlePrimaryProjectChange(val);
                                } else {
                                    handleAddProject(val);
                                }
                                setShowProjectAdder(false);
                            }}
                            value={null}
                            variant="outlined"
                            size="small"
                            showSearch
                            optionFilterProp="label"
                            className="ml-2 w-48 text-xs"
                            popupClassName="dark:bg-[#1e293b]"
                            onBlur={() => setShowProjectAdder(false)}
                            defaultOpen
                        >
                            {(!primaryProjectSelected ? projects : availableProjects).map((p) => (
                                <Select.Option key={p._id} value={p._id} label={p.name}>
                                    <div className="flex items-center gap-2">
                                        <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: p.color || '#65676b' }} />
                                        <span className="text-xs">{p.name}</span>
                                    </div>
                                </Select.Option>
                            ))}
                        </Select>
                    )}
                </div>

                <div className="space-y-2.5">
                    {/* If no primary project is selected, show an inviting trigger to assign project */}
                    {!primaryProjectSelected && !showProjectAdder && canManagePlanning && (
                        <div
                            onClick={() => setShowProjectAdder(true)}
                            className="flex items-center justify-between p-3 rounded-xl border border-dashed border-[#dddfe2] dark:border-[#303030] bg-white/60 dark:bg-[#1f1f1f]/60 hover:bg-white dark:hover:bg-[#1f1f1f] hover:border-[#03488a] cursor-pointer transition-colors"
                        >
                            <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400 text-xs font-medium">
                                <span className="material-symbols-outlined text-base">domain</span>
                                <span>
                                    {isMilestone ? 'Assign this milestone to a project' : 'Assign this task to a project'}...
                                    {mode === 'create' && <span className="text-red-500 font-bold ml-0.5">*</span>}
                                </span>
                            </div>
                            <span className="text-xs font-bold text-[#03488a] dark:text-[#a7c8ff] hover:underline">
                                + Select Project
                            </span>
                        </div>
                    )}

                    {/* Render primary project (if any) and additional projects as equal items */}
                    {task?.project && (
                        <>
                            <ProjectRow
                                project={typeof task.project === 'object' ? task.project : (projects.find(p => String(p._id) === String(task.project)) || { _id: task.project, name: 'Selected Project' })}
                                isPrimary={true}
                                allProjects={projects}
                                onSwitchProject={(newId) => handlePrimaryProjectChange(newId)}
                                onRemove={() => handlePrimaryProjectChange(null)}
                                canManagePlanning={canManagePlanning}
                                editEstHours={editEstHours}
                                setEditEstHours={setEditEstHours}
                                editEstMinutes={editEstMinutes}
                                setEditEstMinutes={setEditEstMinutes}
                                editEffortHours={editEffortHours}
                                setEditEffortHours={setEditEffortHours}
                                editEffortMinutes={editEffortMinutes}
                                setEditEffortMinutes={setEditEffortMinutes}
                                isMilestone={isMilestone}
                                mode={mode}
                            />

                            {/* Milestone selector dropdown for Primary Project */}
                            {!isMilestone && (
                                <div data-tour="task-milestone-parent-select" className="flex items-center gap-2 bg-white dark:bg-[#1f1f1f] border border-[#dddfe2] dark:border-[#303030] rounded-xl px-3.5 py-2 shadow-sm">
                                    <span className="text-amber-500 font-bold text-base shrink-0">◆</span>
                                    <span className="text-[12px] font-semibold text-[#65676b] dark:text-slate-400 shrink-0 flex items-center gap-1">
                                        Milestone
                                        <span className="text-red-500 font-bold ml-0.5">*</span>
                                        {!canManagePlanning && (
                                            <Tooltip title="Only Project Manager or Creator can assign milestones">
                                                <span className="text-xs text-slate-400 cursor-not-allowed">🔒</span>
                                            </Tooltip>
                                        )}
                                    </span>

                                    <Select
                                        placeholder={
                                            <span className="text-[#bec3c9]">
                                                {mergedMilestones.length > 0
                                                    ? `Select Milestone (${mergedMilestones.length} available)...*`
                                                    : 'No milestones in this project'}
                                            </span>
                                        }
                                        value={currentSelectedMilestoneId || undefined}
                                        onChange={(val) => handleMilestoneChange(val)}
                                        allowClear={false}
                                        className="flex-1 text-[13px]"
                                        variant="borderless"
                                        size="small"
                                        showSearch
                                        optionFilterProp="label"
                                        popupClassName="dark:bg-[#1e293b]"
                                        disabled={!canManagePlanning || (mergedMilestones.length === 0 && !currentSelectedMilestoneId)}
                                    >
                                        {mergedMilestones.map((m) => (
                                            <Select.Option key={m._id} value={m._id} label={m.title || m.name}>
                                                <div className="flex items-center gap-2">
                                                    <span className="text-amber-500 font-bold text-xs">◆</span>
                                                    <span className="text-[13px] font-medium text-[#1c1e21] dark:text-white">
                                                        {m.title || m.name}
                                                    </span>
                                                    {m.status && (
                                                        <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-500 ml-auto">
                                                            {m.status}
                                                        </span>
                                                    )}
                                                </div>
                                            </Select.Option>
                                        ))}
                                    </Select>
                                </div>
                            )}
                        </>
                    )}
                    {(additionalProjects || []).map((project) => (
                        <ProjectRow
                            key={project._id || project}
                            project={project}
                            onRemove={() => handleRemoveAdditionalProject(project._id || project)}
                            canManagePlanning={canManagePlanning}
                            editEstHours={editEstHours}
                            setEditEstHours={setEditEstHours}
                            editEstMinutes={editEstMinutes}
                            setEditEstMinutes={setEditEstMinutes}
                            editEffortHours={editEffortHours}
                            setEditEffortHours={setEditEffortHours}
                            editEffortMinutes={editEffortMinutes}
                            setEditEffortMinutes={setEditEffortMinutes}
                            isMilestone={isMilestone}
                        />
                    ))}
                </div>
            </div>

            {/* Effort Bar rendered inside the card section at the very bottom */}
            {hasProjectSelected && !isMilestone && (
                <div className="pt-2 border-t border-[#dddfe2]">
                    <EffortBar
                        estimatedTime={task.estimatedTime}
                        actualTimeMs={displayActualTimeMs}
                        remainingTimeMs={remainingTimeMs}
                    />
                </div>
            )}
        </section>
    );
}

function ProjectRow({
    project,
    onRemove,
    canManagePlanning = true,
    editEstHours,
    setEditEstHours,
    editEstMinutes,
    setEditEstMinutes,
    editEffortHours,
    setEditEffortHours,
    editEffortMinutes,
    setEditEffortMinutes,
    isMilestone,
    isPrimary = false,
    allProjects = [],
    onSwitchProject,
    mode,
}) {
    const {
        task,
        handleBillableStatusChange,
        handleBillingClearanceChange,
        handleCategoryChange,
        handleSubCategoryChange,
    } = useTaskDrawer();
    const [expanded, setExpanded] = useState(true);

    const resolvedProject = (allProjects || []).find((p) => String(p._id) === String(project?._id || project))
        || (typeof project === 'object' ? project : null);

    // Project's own custom categories
    const projectCategories = React.useMemo(() => {
        const list = [];
        if (resolvedProject?.category) list.push(resolvedProject.category);
        if (Array.isArray(resolvedProject?.categories)) {
            resolvedProject.categories.forEach((c) => { if (c && !list.includes(c)) list.push(c); });
        }
        return list;
    }, [resolvedProject]);

    // All selectable categories: project's own first, then task's category, then hardcoded standard ones
    const availableCategories = React.useMemo(() => {
        const list = [];
        projectCategories.forEach((c) => { if (c && !list.includes(c)) list.push(c); });
        if (task?.category && !list.includes(task.category)) list.push(task.category);
        STANDARD_CATEGORIES.forEach((c) => { if (!list.includes(c)) list.push(c); });
        return list;
    }, [projectCategories, task?.category]);

    // Subcategories available based on the selected category or project
    const availableSubCategories = React.useMemo(() => {
        const list = [];
        if (Array.isArray(resolvedProject?.subcategories)) {
            resolvedProject.subcategories.forEach((s) => { if (s && !list.includes(s)) list.push(s); });
        }
        const catKey = task?.category || (mode === 'create' ? resolvedProject?.category : resolvedProject?.category);
        if (catKey && CATEGORIES_WITH_SUBCATEGORIES[catKey]) {
            CATEGORIES_WITH_SUBCATEGORIES[catKey].forEach((s) => { if (!list.includes(s)) list.push(s); });
        }
        if (task?.subCategory && !list.includes(task.subCategory)) list.push(task.subCategory);
        return list;
    }, [resolvedProject, task?.category, task?.subCategory, mode]);

    const projectCategory = task?.category || resolvedProject?.category || project?.category || 'General';

    const projName = (project?.name || project?.title || task?.project?.name || '').toLowerCase();
    const isTrevorProject = projName.includes("trevor's website projects") || projName.includes("trevor");

    return (
        <div className="rounded-xl border border-[#dddfe2] dark:border-[#303030] bg-white dark:bg-[#1f1f1f] overflow-hidden">
            {/* Project header */}
            <div
                onClick={() => setExpanded(v => !v)}
                className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-[#f7f8fa] dark:hover:bg-[#262626] transition-colors"
            >
                <div className="flex items-center gap-2.5 min-w-0 flex-1">
                    <span
                        className="material-symbols-outlined text-[#65676b] transition-transform duration-200 shrink-0"
                        style={{ fontSize: '16px', transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)' }}
                    >
                        chevron_right
                    </span>
                    <div
                        className="w-3.5 h-3.5 rounded-full shrink-0"
                        style={{ backgroundColor: project?.color || '#03488a', borderRadius: '4px' }}
                    />
                    <Tooltip title={project?.name || project?.title || 'Associated Project'} placement="top">
                        <span className="text-[13px] font-semibold text-[#1c1e21] dark:text-white truncate">
                            {project?.name || project?.title || 'Associated Project'}
                        </span>
                    </Tooltip>
                    {isPrimary && (
                        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-blue-50 dark:bg-blue-950/80 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800 shrink-0">
                            Primary
                        </span>
                    )}
                </div>

                {isPrimary ? (
                    canManagePlanning && (
                        <div onClick={(e) => e.stopPropagation()} className="flex items-center gap-1.5 shrink-0 ml-2">
                            {(allProjects || []).length > 1 && (
                                <Select
                                    placeholder="Switch project"
                                    value={project?._id || project}
                                    onChange={(val) => onSwitchProject?.(val)}
                                    size="small"
                                    showSearch
                                    optionFilterProp="label"
                                    style={{ minWidth: 200 }}
                                    className="text-xs min-w-[200px]"
                                    popupClassName="dark:bg-[#1e293b]"
                                >
                                    {(allProjects || []).map((p) => (
                                        <Select.Option key={p._id} value={p._id} label={p.name}>
                                            <div className="flex items-center gap-2">
                                                <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: p.color || '#65676b' }} />
                                                <span className="text-xs whitespace-nowrap">{p.name}</span>
                                            </div>
                                        </Select.Option>
                                    ))}
                                </Select>
                            )}
                            {(mode === 'create' || task?.isDraft || task?.status === 'Draft') && onRemove && (
                                <button
                                    onClick={(e) => { e.stopPropagation(); onRemove?.(); }}
                                    className="text-[#65676b] hover:text-red-500 transition-colors shrink-0 ml-1"
                                    title="Clear selected project"
                                >
                                    <span className="material-symbols-outlined text-base">close</span>
                                </button>
                            )}
                        </div>
                    )
                ) : canManagePlanning && onRemove ? (
                    <button
                        onClick={(e) => { e.stopPropagation(); onRemove?.(); }}
                        className="text-[#65676b] hover:text-red-500 transition-colors shrink-0 ml-2"
                        title="Remove project"
                    >
                        <span className="material-symbols-outlined text-base">close</span>
                    </button>
                ) : (
                    <Tooltip title="Project assignment is managed by Project Manager">
                        <span className="text-xs text-slate-400 ml-2 cursor-not-allowed">🔒</span>
                    </Tooltip>
                )}
            </div>

            {/* Expanded details table */}
            {expanded && (
                <div className="border-t border-[#e9ebee]">
                    <table className="w-full text-[13px] border-collapse">
                        <tbody>
                            <tr className="border-b border-[#e9ebee]">
                                <td className="px-4 py-2.5 text-[#65676b] w-1/2">
                                    <div className="flex items-center gap-2">
                                        <span className="material-symbols-outlined text-base">check_circle</span>
                                        QA Testing
                                    </div>
                                </td>
                                <td className="px-4 py-2.5 text-[#65676b] border-l border-[#e9ebee] w-1/2">—</td>
                            </tr>
                            <tr className="border-b border-[#e9ebee]">
                                <td className="px-4 py-2.5 text-[#65676b] w-1/2">
                                    <div className="flex items-center gap-2">
                                        <span className="material-symbols-outlined text-base">schedule</span>
                                        Maintenance
                                    </div>
                                </td>
                                <td className="px-4 py-2.5 text-[#65676b] border-l border-[#e9ebee] w-1/2">—</td>
                            </tr>

                            {/* Billable Status & Billing Clearance (Scoped to Trevor's Website Projects) */}
                            {isTrevorProject && (
                                <>
                                    <tr className="border-b border-[#e9ebee] dark:border-[#303030]">
                                        <td className="px-4 py-2.5 text-[#65676b] dark:text-slate-400 w-1/2">
                                            <div className="flex items-center gap-2 font-medium">
                                                <span className="material-symbols-outlined text-base text-[#65676b] dark:text-slate-400">payments</span>
                                                Billable Status
                                            </div>
                                        </td>
                                        <td className="px-4 py-1.5 text-[#1c1e21] dark:text-white border-l border-[#e9ebee] dark:border-[#303030] w-1/2">
                                            <Select
                                                value={task?.billableStatus || 'Billable'}
                                                onChange={handleBillableStatusChange}
                                                variant="borderless"
                                                size="small"
                                                className="w-full"
                                                style={{ paddingLeft: 0, marginLeft: '-8px' }}
                                                popupClassName="dark:bg-[#1e293b]"
                                            >
                                                <Select.Option value="Billable">
                                                    <div className="flex items-center gap-2">
                                                        <span className="w-2 h-2 rounded-full bg-emerald-500 shrink-0" />
                                                        <span className="text-[13px] font-medium text-emerald-700 dark:text-emerald-400">Billable</span>
                                                    </div>
                                                </Select.Option>
                                                <Select.Option value="Non Billable">
                                                    <div className="flex items-center gap-2">
                                                        <span className="w-2 h-2 rounded-full bg-slate-400 shrink-0" />
                                                        <span className="text-[13px] font-medium text-slate-600 dark:text-slate-300">Non Billable</span>
                                                    </div>
                                                </Select.Option>
                                                <Select.Option value="Internal">
                                                    <div className="flex items-center gap-2">
                                                        <span className="w-2 h-2 rounded-full bg-indigo-500 shrink-0" />
                                                        <span className="text-[13px] font-medium text-indigo-600 dark:text-indigo-400">Internal</span>
                                                    </div>
                                                </Select.Option>
                                            </Select>
                                        </td>
                                    </tr>

                                    <tr className="border-b border-[#e9ebee] dark:border-[#303030]">
                                        <td className="px-4 py-2.5 text-[#65676b] dark:text-slate-400 w-1/2">
                                            <div className="flex items-center gap-2 font-medium">
                                                <span className="material-symbols-outlined text-base text-[#65676b] dark:text-slate-400">price_check</span>
                                                Billing Clearance
                                            </div>
                                        </td>
                                        <td className="px-4 py-1.5 text-[#1c1e21] dark:text-white border-l border-[#e9ebee] dark:border-[#303030] w-1/2">
                                            <Select
                                                value={task?.billingClearance || 'Unbilled'}
                                                onChange={handleBillingClearanceChange}
                                                variant="borderless"
                                                size="small"
                                                className="w-full"
                                                style={{ paddingLeft: 0, marginLeft: '-8px' }}
                                                popupClassName="dark:bg-[#1e293b]"
                                            >
                                                <Select.Option value="Unbilled">
                                                    <div className="flex items-center gap-2">
                                                        <span className="w-2 h-2 rounded-full bg-amber-500 shrink-0" />
                                                        <span className="text-[13px] font-medium text-amber-600 dark:text-amber-400">Unbilled</span>
                                                    </div>
                                                </Select.Option>
                                                <Select.Option value="Cleared/Paid">
                                                    <div className="flex items-center gap-2">
                                                        <span className="w-2 h-2 rounded-full bg-emerald-500 shrink-0" />
                                                        <span className="text-[13px] font-medium text-emerald-700 dark:text-emerald-400">Cleared/Paid</span>
                                                    </div>
                                                </Select.Option>
                                            </Select>
                                        </td>
                                    </tr>
                                </>
                            )}

                            {/* ── Category ── */}
                            <tr className="border-b border-[#e9ebee] dark:border-[#303030]">
                                <td className="px-4 py-2.5 text-[#65676b] dark:text-slate-400 w-1/2">
                                    <div className="flex items-center gap-2 font-medium">
                                        <span className="material-symbols-outlined text-base text-[#65676b] dark:text-slate-400">category</span>
                                        Category
                                    </div>
                                </td>
                                <td className="px-4 py-1.5 text-[#1c1e21] dark:text-white border-l border-[#e9ebee] dark:border-[#303030] w-1/2">
                                    <Select
                                        showSearch
                                        allowClear
                                        placeholder="Select Category…"
                                        value={task?.category || (mode === 'create' && resolvedProject?.category ? resolvedProject.category : undefined)}
                                        onChange={(val) => handleCategoryChange?.(val || null)}
                                        variant="borderless"
                                        size="small"
                                        className="w-full text-xs font-medium"
                                        style={{ paddingLeft: 0, marginLeft: '-8px' }}
                                        popupClassName="dark:bg-[#1e293b]"
                                        optionFilterProp="label"
                                    >
                                        {availableCategories.map((cat) => (
                                            <Select.Option key={cat} value={cat} label={cat}>
                                                <div className="flex items-center justify-between">
                                                    <span className="text-xs">{cat}</span>
                                                    {projectCategories.includes(cat) && (
                                                        <span className="text-[10px] font-semibold text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/60 px-1.5 py-0.5 rounded ml-2">
                                                            Project
                                                        </span>
                                                    )}
                                                </div>
                                            </Select.Option>
                                        ))}
                                    </Select>
                                </td>
                            </tr>

                            {/* ── Sub-Category ── */}
                            {availableSubCategories.length > 0 && (
                                <tr className="border-b border-[#e9ebee] dark:border-[#303030]">
                                    <td className="px-4 py-2.5 text-[#65676b] dark:text-slate-400 w-1/2">
                                        <div className="flex items-center gap-2 font-medium">
                                            <span className="material-symbols-outlined text-base text-[#65676b] dark:text-slate-400">subdirectory_arrow_right</span>
                                            Sub-Category
                                        </div>
                                    </td>
                                    <td className="px-4 py-1.5 text-[#1c1e21] dark:text-white border-l border-[#e9ebee] dark:border-[#303030] w-1/2">
                                        <Select
                                            showSearch
                                            allowClear
                                            placeholder="Select Sub-Category…"
                                            value={task?.subCategory || undefined}
                                            onChange={(val) => handleSubCategoryChange?.(val || null)}
                                            variant="borderless"
                                            size="small"
                                            className="w-full text-xs font-medium"
                                            style={{ paddingLeft: 0, marginLeft: '-8px' }}
                                            popupClassName="dark:bg-[#1e293b]"
                                            optionFilterProp="label"
                                        >
                                            {availableSubCategories.map((sub) => (
                                                <Select.Option key={sub} value={sub} label={sub}>
                                                    <span className="text-xs">{sub}</span>
                                                </Select.Option>
                                            ))}
                                        </Select>
                                    </td>
                                </tr>
                            )}

                            {/* Estimated Time Input */}
                            <tr data-tour="task-effort-estimate" className="border-b border-[#e9ebee] dark:border-[#303030]">
                                <td className="px-4 py-2.5 text-[#65676b] dark:text-slate-400 w-1/2">
                                    <div className="flex items-center gap-2 font-medium">
                                        <span className="text-[#65676b] dark:text-slate-400 font-bold text-base">#</span>
                                        Estimated Time
                                        {!canManagePlanning && (
                                            <Tooltip title="Estimated time is locked by Project Manager">
                                                <span className="text-xs text-slate-400 cursor-not-allowed ml-1">🔒</span>
                                            </Tooltip>
                                        )}
                                    </div>
                                </td>
                                <td className="px-4 py-2 text-[#1c1e21] dark:text-white border-l border-[#e9ebee] dark:border-[#303030] w-1/2">
                                    <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                                        <input
                                            type="number" min={0}
                                            disabled={!canManagePlanning}
                                            value={editEstHours || 0}
                                            onChange={(e) => setEditEstHours(Math.max(0, parseInt(e.target.value, 10) || 0))}
                                            className={`w-14 bg-[#f0f2f5] dark:bg-[#141414] hover:bg-[#e4e6e9] dark:hover:bg-[#262626] focus:bg-white dark:focus:bg-[#141414] border border-transparent focus:border-[#dddfe2] dark:focus:border-[#303030] rounded px-1.5 py-0.5 text-[13px] font-semibold text-[#1c1e21] dark:text-white text-center outline-none transition-all ${!canManagePlanning ? 'cursor-not-allowed opacity-60' : ''}`}
                                        />
                                        <span className="text-[12px] text-[#65676b] dark:text-slate-400">h</span>
                                        <input
                                            type="number" min={0} max={59}
                                            disabled={!canManagePlanning}
                                            value={editEstMinutes || 0}
                                            onChange={(e) => setEditEstMinutes(Math.min(59, Math.max(0, parseInt(e.target.value, 10) || 0)))}
                                            className={`w-14 bg-[#f0f2f5] dark:bg-[#141414] hover:bg-[#e4e6e9] dark:hover:bg-[#262626] focus:bg-white dark:focus:bg-[#141414] border border-transparent focus:border-[#dddfe2] dark:focus:border-[#303030] rounded px-1.5 py-0.5 text-[13px] font-semibold text-[#1c1e21] dark:text-white text-center outline-none transition-all ${!canManagePlanning ? 'cursor-not-allowed opacity-60' : ''}`}
                                        />
                                        <span className="text-[12px] text-[#65676b] dark:text-slate-400">m</span>
                                    </div>
                                </td>
                            </tr>

                            {/* Effort Time Input */}
                            <tr>
                                <td className="px-4 py-2.5 text-[#65676b] dark:text-slate-400 w-1/2">
                                    <div className="flex items-center gap-2 font-medium">
                                        <span className="text-[#65676b] dark:text-slate-400 font-bold text-base">#</span>
                                        Effort Time
                                    </div>
                                </td>
                                <td className="px-4 py-2 text-[#1c1e21] dark:text-white border-l border-[#e9ebee] dark:border-[#303030] w-1/2">
                                    <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                                        <input
                                            type="number" min={0}
                                            value={editEffortHours || 0}
                                            onChange={(e) => setEditEffortHours(Math.max(0, parseInt(e.target.value, 10) || 0))}
                                            className="w-14 bg-[#f0f2f5] dark:bg-[#141414] hover:bg-[#e4e6e9] dark:hover:bg-[#262626] focus:bg-white dark:focus:bg-[#141414] border border-transparent focus:border-[#dddfe2] dark:focus:border-[#303030] rounded px-1.5 py-0.5 text-[13px] font-semibold text-[#1c1e21] dark:text-white text-center outline-none transition-all"
                                        />
                                        <span className="text-[12px] text-[#65676b] dark:text-slate-400">h</span>
                                        <input
                                            type="number" min={0} max={59}
                                            value={editEffortMinutes || 0}
                                            onChange={(e) => setEditEffortMinutes(Math.min(59, Math.max(0, parseInt(e.target.value, 10) || 0)))}
                                            className="w-14 bg-[#f0f2f5] dark:bg-[#141414] hover:bg-[#e4e6e9] dark:hover:bg-[#262626] focus:bg-white dark:focus:bg-[#141414] border border-transparent focus:border-[#dddfe2] dark:focus:border-[#303030] rounded px-1.5 py-0.5 text-[13px] font-semibold text-[#1c1e21] dark:text-white text-center outline-none transition-all"
                                        />
                                        <span className="text-[12px] text-[#65676b] dark:text-slate-400">m</span>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
